"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = OnboardingNotice;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_i18next_1 = require("react-i18next");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const UserContext_1 = require("../contexts/UserContext");
const SingleSignOn_1 = require("../shared/SingleSignOn");
const Card_1 = __importDefault(require("../components/Card"));
const MainMenuItem_1 = __importDefault(require("../components/MainMenuItem"));
const Loading_1 = __importDefault(require("../components/Loading"));
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const FirebaseAnalytics_1 = __importDefault(require("../shared/FirebaseAnalytics"));
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
function OnboardingNotice(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const refLoading = react_1.default.useRef(null);
    const refTimer = react_1.default.useRef(null);
    const refCheckCount = react_1.default.useRef(0);
    const [focusedApp, setFocusedApp] = react_1.default.useState(true);
    const [focusedNav, setFocusedNav] = react_1.default.useState(true);
    const working = (userContext.initializing ||
        userContext.employeeLoading ||
        userContext.clientLoading);
    const load = () => {
        refCheckCount.current += 1;
        refLoading.current = userContext.refreshEmployee()
            .then((ctx) => {
            let client = ctx?.result?.clients?.[userContext?.client?.id];
            if (client?.onboarding == false)
                ctx?.update();
        }).finally(() => {
            delay();
        });
    };
    const delay = react_1.default.useCallback(() => {
        // Only continue to poll if app is focused.
        if (focusedApp && focusedNav) {
            if (refCheckCount.current < 6) {
                refTimer.current = setTimeout(load, 10 * 1000);
            }
        }
    }, [focusedApp, focusedNav]);
    // Web: Listens for browser tab focused or not focused.
    // Mobile: Listens for app being in background or foreground.
    react_1.default.useEffect(() => {
        var event = react_native_1.AppState.addEventListener('change', (state) => {
            if (state == 'active') {
                setFocusedApp(true);
            }
            else {
                setFocusedApp(false);
            }
        });
        return () => event.remove();
    }, []);
    // Mobile: Listens for navigating to this screen.
    react_1.default.useEffect(() => {
        return props.navigation.addListener('focus', () => {
            setFocusedNav(true);
        });
    }, []);
    // Mobile: Listens for navigating away from this screen.
    react_1.default.useEffect(() => {
        return props.navigation.addListener('blur', () => {
            setFocusedNav(false);
        });
    }, []);
    react_1.default.useEffect(() => {
        if (focusedApp && focusedNav) {
            refCheckCount.current = 0;
            load();
            return () => {
                refLoading.current?.cancel();
                if (refTimer.current) {
                    clearTimeout(refTimer.current);
                    refTimer.current = null;
                }
            };
        }
    }, [focusedApp, focusedNav]);
    const gotoOnboarding = async () => {
        FirebaseAnalytics_1.default.LogEvent('feature_used', {
            'feature': 'Prism SSO - Employee Onboarding',
            'client': userContext.client?.id,
            'employee': userContext.employee?.employeeId
        });
        refCheckCount.current = 0;
        SingleSignOn_1.SingleSignOn.Perform({
            navigation: props.navigation,
            client: userContext?.client?.id,
            provider: 'prism',
            additionalRouting: 'ONBOARDING',
            title: t('register:buttons.onboarding')
        });
    };
    return ((0, jsx_runtime_1.jsx)(Card_1.default, { style: {
            marginHorizontal: (size != 'sm' ? 12 : 0),
            paddingHorizontal: (size != 'sm' ? 24 : 8),
            paddingVertical: (size != 'sm' ? 24 : 12),
            paddingBottom: (size != 'sm' ? 24 : 16)
        }, children: userContext.isOnboarding
            &&
                (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: {
                                flexDirection: 'row',
                                marginVertical: 8,
                                alignItems: 'center',
                                justifyContent: 'center'
                            }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: {
                                        color: THEME.Text, fontSize: (size != 'sm' ? 36 : 26),
                                        fontWeight: '500', marginTop: -8, marginRight: 8
                                    }, children: t('register:headers.welcome') }), (0, jsx_runtime_1.jsx)(Theme_1.NextepLogo, { height: size != 'sm' ? 40 : 30, width: size != 'sm' ? 160 : 100 })] }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { margin: 12 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: (size != 'sm' ? 16 : 14), fontWeight: '400' }, children: t('register:text.onboardInfo') }) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: {
                                marginVertical: 12,
                                marginHorizontal: 12
                            }, children: working
                                &&
                                    (0, jsx_runtime_1.jsx)(Loading_1.default, { loading: working, retryable: false })
                                ||
                                    (0, jsx_runtime_1.jsx)(MainMenuItem_1.default, { disabled: working, icon: Theme_1.ICONS.Solid.IdCardClip, text: t('register:buttons.onboarding'), actionText: t('register:buttons.start'), onPress: gotoOnboarding }) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { margin: 12 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: (size != 'sm' ? 16 : 14), fontWeight: '400' }, children: t('register:text.onboardComplete') }) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { margin: 12, marginBottom: 0 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: (size != 'sm' ? 16 : 14), fontWeight: '400' }, children: t('register:text.onboardAfter') }) })] })
            ||
                (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center', margin: 12 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { flex: 1, color: THEME.Text, fontWeight: '500', fontSize: (size != 'sm' ? 20 : 18) }, children: t('register:buttons.onboarding') }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.Check, size: 14, color: THEME.Secondary, style: { marginRight: 8 } }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Secondary, fontSize: 14, fontWeight: '500' }, children: "Completed" })] })] }) }));
}
;
